import React, { useState, useRef, useEffect } from "react";
import { Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SowHeader from "../../components/SowHeader/SowHeader";
import { projectChromeQueryArgs } from "../../components/SowHeader/projectChromeQuery";
import useFlags from "@common/hooks/useFlags";
import { useV2GetProjectByIdQuery, useV2GetAccountQuery } from "@generated";
import { useProjectCounts } from "@common/hooks/useProjectCounts";
import { useChangeOrderAccess } from "../../pages/ChangeOrders/hooks/useChangeOrderAccess";
import ChangeOrderBanner from "../../pages/ChangeOrders/ChangeOrderBanner/ChangeOrderBanner";
import { changeOrderChild } from "../../pages/ChangeOrders/utils/changeOrderChild";
import ProjectNavChildLink from "./ProjectNavChildLink";
import { resolveProjectPath, visibleNavSections } from "./projectNavItems";

interface ProjectNavigationProviderProps {
  account_slug: string;
  sidebarWidth: number;
  projectId: string;
  children: React.ReactNode;
}

const tierOneStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 0,
  fontFamily: "Open Sans",
  fontSize: 14,
  fontWeight: 700,
  position: "relative",
};

const tierOneItemStyle = (isActive: boolean): React.CSSProperties => ({
  padding: "10px 16px",
  color: "#1C2655",
  backgroundColor: isActive ? "#F8F9FA" : "transparent",
  borderRadius: isActive ? "4px 4px 0 0" : 0,
  cursor: "default",
  whiteSpace: "nowrap",
  textDecoration: "none",
  transition: "background-color 0.15s ease",
});

const tierTwoStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  display: "flex",
  flexDirection: "column",
  minWidth: 200,
  backgroundColor: "#F8F9FA",
  border: "1px solid #D7D7D7",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  fontFamily: "Open Sans, sans-serif",
  fontSize: 13,
  zIndex: 9,
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  padding: "4px 0",
};

const tierTwoItemStyle: React.CSSProperties = {
  padding: "8px 16px",
  color: "#1C2655",
  whiteSpace: "nowrap",
  textDecoration: "none",
  display: "block",
  transition: "background-color 0.15s ease",
};

const ProjectNavigationProvider: React.FC<ProjectNavigationProviderProps> = ({
  account_slug,
  sidebarWidth,
  projectId,
  children,
}) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  const { saveToCloudStorageEnable, showResourcePlanner } = useFlags();

  // Some nav children are only shown for certain project statuses (e.g. the
  // PSA Project link is only relevant once the project is approved/won).
  //
  // Args shared with SowHeader (see projectChromeQuery) so both read one
  // cache entry; the psa-project include is what makes its linkage appear.
  const { data: projectData } = useV2GetProjectByIdQuery(
    projectChromeQueryArgs(projectId)
  );
  const projectStatus = projectData?.data?.attributes?.status;
  const hasPsaProject = Boolean(
    projectData?.data?.relationships?.["psa-project"]?.data
  );
  const { isChangeOrder, parentChangeOrderId } = changeOrderChild(
    projectData?.data
  );

  // `psa?` is true only when the account has an enabled PsaIntegration -- the
  // record the Connected Apps toggle writes. Having a ConnectWise connection is
  // not enough: it supports PSA, CRM and Product, and gets no PSA row until
  // someone turns the capability on. This is the same attribute SowHeader gates
  // its Push To PSA button on, so the two agree.
  const { data: accountData } = useV2GetAccountQuery({});
  const psaEnabled = Boolean(accountData?.data?.attributes?.["psa?"]);

  const { navVisible: changeOrdersVisible } = useChangeOrderAccess();
  const counts = useProjectCounts(projectId, account_slug);

  const visibleNavItems = visibleNavSections({
    projectStatus,
    psaEnabled,
    hasPsaProject,
    changeOrdersVisible,
    resourcePlannerVisible: showResourcePlanner === true,
    entitlements: accountData?.data?.attributes?.entitlements,
  });

  useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setHeaderHeight(entry.contentRect.height);
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSectionEnter = (sectionId: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveSection(sectionId);
  };

  const handleNavLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveSection(null);
    }, 150);
  };

  const handleTierTwoEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <Card style={{ border: "none", borderRadius: 0 }}>
      {/* Fixed card header */}
      <div
        ref={headerRef}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          left: sidebarWidth,
          zIndex: 100,
          backgroundColor: "#fff",
          borderBottom: "1px solid #D7D7D7",
          transition: "left 0.2s ease",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            border: "none",
            borderRadius: 0,
            padding: 0,
          }}
        >
          {/* SowHeader */}
          <SowHeader
            project_id={projectId}
            account_slug={account_slug}
            save_to_cloud_storage_flag={saveToCloudStorageEnable}
          />

          {/* Change-order child projects name their parent */}
          <ChangeOrderBanner
            isChangeOrder={isChangeOrder}
            parentChangeOrderId={parentChangeOrderId}
          />

          {/* Tier 1 navigation */}
          <div style={tierOneStyle} onMouseLeave={handleNavLeave}>
            {visibleNavItems.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <div
                  key={section.id}
                  style={{
                    ...tierOneItemStyle(isActive),
                    position: "relative",
                  }}
                  onMouseEnter={() => handleSectionEnter(section.id)}
                >
                  {section.label}

                  {/* Tier 2 — vertical dropdown */}
                  {isActive && section.children.length > 0 && (
                    <div
                      style={tierTwoStyle}
                      onMouseEnter={handleTierTwoEnter}
                      onMouseLeave={handleNavLeave}
                    >
                      {section.children.map((child) => {
                        const resolvedPath = resolveProjectPath(
                          child.path,
                          projectId
                        );
                        return (
                          <ProjectNavChildLink
                            key={child.id}
                            child={child}
                            count={
                              child.countKey
                                ? counts?.[child.countKey]
                                : undefined
                            }
                            href={resolvedPath}
                            style={tierTwoItemStyle}
                            onNavigate={(e) => {
                              if (child.routing === "react") {
                                e.preventDefault();
                                navigate(resolvedPath);
                                setActiveSection(null);
                              }
                              // rails routing: default anchor behavior (full page GET)
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card.Header>
      </div>

      {/* Spacer to push body below the fixed header */}
      <div style={{ height: headerHeight }} />

      {/* Card body — scrollable content */}
      <Card.Body style={{ padding: 0 }}>{children}</Card.Body>
    </Card>
  );
};

export default ProjectNavigationProvider;
